import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from 'vue';
import PageLayout from "@/components_v3/layout/PageLayout.vue";
import CreatePrivateRateHead from "@/components_v3/offers/private-rates/CreatePrivateRateHead.vue";
import BaseButtonV2 from "@/components-UI/buttons/BaseButtonV2.vue";
import SvgCheck from "@/assets/icons/check.svg";
import SvgCancel from "@/assets/icons/reload.svg";
import CreatePrivateRateGoals from "@/components_v3/offers/private-rates/CreatePrivateRateGoals.vue";
import SvgAttention from "@/assets/icons/attention-symbol.svg";
import { usePrivateRatesStore } from "@/store_v3/PrivateRatesStore";
import { storeToRefs } from "pinia";
import { createPrivateRate, updatePrivateRate, getPrivateRateSingle } from "@/api/offers";
import { CreatePrivateRate, CreatePrivateRatePrices } from "@/api/types/offers";
import { showNotification, showServerError } from "@/utils";
import BaseConfirmModal from "@/components-UI/modals/BaseConfirmModal.vue";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router/composables";
import { OFFER_TYPE_MARKING } from "@/mappings/offer";
import { parse } from "date-fns";
import { updateQueryString } from "@/utils/updateQueryString";
import { USER_ROLES } from "@/mappings/user-roles";
import store from "@/store";


export default /*#__PURE__*/_defineComponent({
  __name: 'CreatePrivateRate',
  setup(__props) {

const {
    createForm,
    changedGoals,
    getOfferTags,
    editableActiveRates,
    isIncludeDepartment,
    isOfferTrafficAPI,
} = storeToRefs(usePrivateRatesStore());
const {
    clearForm,
} = usePrivateRatesStore();

const route = useRoute();
const router = useRouter();
const isSaved = ref(false);

const conflictTags = new Set([
    OFFER_TYPE_MARKING.Above,
    OFFER_TYPE_MARKING.FromRate,
    OFFER_TYPE_MARKING.AlwaysFromRate,
    OFFER_TYPE_MARKING.None,
]);
const tagsAndMarkings = computed(() => {
    const conflictsCount = getOfferTags.value.filter(t => conflictTags.has(t)).length;

    return {
        isNoMarkings: !conflictsCount,
        isMoreThanOneTag: conflictsCount > 1,
    };
});

const isManagerOffer = computed(() => {
    const user = store.getters['authModule/GET_USER'];
    const isAdmin = [USER_ROLES.ADMIN, USER_ROLES.SUPERVISOR].some(role => user.roles.includes(role));
    if (isAdmin) return true;

    const isManager = user.roles.includes(USER_ROLES.ACCOUNT_MANAGER);
    const managerId = createForm.value.offerObj?.manager?.id;
    if (isManager && user.id === managerId) return true;

    const managers = createForm.value.advertiserObj?.managers ?? [];
    return managers.some(m => m.manager_id === user.account_id);
});

const isOfferAndDefaultRateOrAffiliate = computed(() => {
    const { offerId, affiliateId, defaultRate } = createForm.value;

    return !offerId || (!defaultRate ? !(!defaultRate && affiliateId) : !defaultRate);
});

const isEmpty = (v: unknown) => {
    return v === null || v === undefined || v === '';
};

const notCheckedItemAndNotValidDate = computed(() => {
    if (!changedGoals.value.length) return false;

    for (const i of changedGoals.value) {
        if (
            isEmpty(i.full_price) ||
            isEmpty(i.price_after_vat) ||
            isEmpty(i.income) ||
            isEmpty(i.revenue) ||
            isEmpty(i.start_date)
        ) {
            return false;
        }
    }

    return true;
});

const isDatesConflict = computed(() => {
    return changedGoals.value.some(goal => {
        const rate = createForm.value.activeRates.find(r => r.goal_id === goal.legacy_id);
        if (!rate) return false;
        const rateDate = parse(rate.start_date, 'yyyy-MM-dd HH:mm:ss', new Date());
        const goalDate = parse(goal.start_date, 'yyyy-MM-dd HH:mm', new Date());
        return rateDate > goalDate;
    });
});

const getErrorParams = computed(() => {
    const errors: string[] = [];
    // ЕСЛИ НАПРАВЛЕНИ (РФ) - Не пустое поле тегов
    if (!isIncludeDepartment.value && tagsAndMarkings.value.isNoMarkings) errors.push('В оффере не указан тип маркировки');
    if (isDatesConflict.value) errors.push('Пересекаются даты активной цены и новой');
    if (!isManagerOffer.value) errors.push('Вы не являетесь менеджером данного оффера');

    return {
        mainAlert: createForm.value.offerId &&
            (
                (
                    // ЕСЛИ НАПРАВЛЕНИЕ (РФ) - 1 ключевой тег И не пустое поле тегов
                    !isIncludeDepartment.value && (
                        tagsAndMarkings.value.isNoMarkings ||
                            tagsAndMarkings.value.isMoreThanOneTag
                    )
                ) ||
                isDatesConflict.value ||
                !isManagerOffer.value
            ),
        // ЕСЛИ НАПРАВЛЕНИЕ (РФ) - 1 ключевой тег И не пустое поле тегов
        linkErrorText: !isIncludeDepartment.value &&
            (
                tagsAndMarkings.value.isMoreThanOneTag ||
                    tagsAndMarkings.value.isNoMarkings
            ),
        errorText: errors.join(', '),
    };
});

const isDisableSaveButtons = computed(() => {
    /*
        Не выбран оффер
        Если "Цена по умолчанию" выключена
            Свитч "Цена по умолчанию" выключен и не выбран партнёр
        Иначе "Цена по умолчанию" включена
    */
    return isOfferAndDefaultRateOrAffiliate.value ||
        (
            // ЕСЛИ НАПРАВЛЕНИЕ (РФ) - Только 1 ключевой тег И НЕ пустое поле тегов
            !isIncludeDepartment.value &&
                (
                    tagsAndMarkings.value.isNoMarkings ||
                        tagsAndMarkings.value.isMoreThanOneTag
                )
        ) ||
        // Ни одна цель не отмечена чекбоксоме
        // В одной из отмеченных чекбоксом целей не заполнены обязательные поля в секции “Новая цена” (обязательны все инпуты, кроме даты окончания)
        !notCheckedItemAndNotValidDate.value ||
        // В одной из отмеченных чекбоксом целей дата начала новой цены раньше, чем дата начала старой цены
        isDatesConflict.value ||
        !isOfferTrafficAPI;
});

const saveAndBackToList = async (): Promise<void> => {
    await sendForm().then((r) => {
        if (r) {
            isSaved.value = true;
            router.push({ name: 'private-rates' });
        }
    });
};

const saveAndAddMore = async (): Promise<void> => {
    await sendForm().then((r) => {
        if (r) {
            const isMultiple = changedGoals.value.length > 1;
            const literal = isMultiple ? 'ы' : 'а';
            const text = `Цен${literal} успешно создан${literal}`;
            showNotification(text);
            clearForm();
        }
    });
};

const resetAndBackToList = (): void => {
    router.push({ name: 'private-rates' });
};

const sendForm = async (): Promise<boolean> => {
    try {
        await updateActiveRate();
        let prices: CreatePrivateRatePrices[] = [];
        prices = changedGoals.value.map(e => {
            return {
                goal_id: e.legacy_id,
                full_price: Number(e.full_price),
                price_after_vat: Number(e.price_after_vat),
                revenue: Number(e.revenue),
                income: Number(e.income),
                start_date: e.start_date ? e.start_date : null,
                end_date: e.end_date ? e.end_date : null,
            };
        });

        const data: CreatePrivateRate = {
            offer_id: createForm.value.offerId as number,
            affiliate_id: createForm.value.affiliateId as number,
            type: createForm.value.type,
            prices,
        };

        await createPrivateRate(data);
        return true;
    } catch (e) {
        showServerError(e, 'Цена не сохранена');
        return false;
    }
};

const updateActiveRate = async () => {
    try {
        await Promise.all(Object.values(editableActiveRates.value).map(({ id, end_date }) => updatePrivateRate(id, { end_date })));
    } catch (err) {
        showServerError(err, 'Не удалось обновить актуальные цены');
    }
};

const isShow = ref(false);
const isShowTable = computed(() => {
    return isManagerOffer.value && isShow.value && createForm.value.offerId && (createForm.value.affiliateId || createForm.value.defaultRate);
});
const updateTable = () => {
    isShow.value = false;
    setTimeout(() => isShow.value = true, 100);
};

// --Leave
const confirmModal = ref(null);
onBeforeRouteLeave(async (to, from, next) => {
    if (isSaved.value) next();
    const message = 'Изменения не сохранены. Вы действительно хотите покинуть страницу?';
    const isLeave = await confirmModal.value?.openModal(message);
    if (isLeave) {
        next();
    } else {
        next(false);
    }
});

watch(() => router.currentRoute.query.copy, async (copyId: string | undefined): Promise<void> => {
    if (copyId) {
        await getPrivateRateSingle(copyId)
            .then(({ offer_id, type, affiliate_id }: any) => {
                createForm.value.offerId = offer_id;

                setTimeout(() => {
                    createForm.value.type = type;
                }, 500);

                if (affiliate_id) {
                    createForm.value.defaultRate = false;
                    createForm.value.affiliateId = affiliate_id;
                }
            })
            .catch(e => {
                showServerError(e, 'Цена не получена');
            });
    }
}, { immediate: true, deep: true });

onMounted((): void => {
    const isFromDetail = ['affiliate', 'offer'].includes(route.query?.from!);
    if (isFromDetail) {
        updateQueryString({});
    } else {
        clearForm();
    }
});

return { __sfc: true,createForm, changedGoals, getOfferTags, editableActiveRates, isIncludeDepartment, isOfferTrafficAPI, clearForm, route, router, isSaved, conflictTags, tagsAndMarkings, isManagerOffer, isOfferAndDefaultRateOrAffiliate, isEmpty, notCheckedItemAndNotValidDate, isDatesConflict, getErrorParams, isDisableSaveButtons, saveAndBackToList, saveAndAddMore, resetAndBackToList, sendForm, updateActiveRate, isShow, isShowTable, updateTable, confirmModal, PageLayout, CreatePrivateRateHead, BaseButtonV2, SvgCheck, SvgCancel, CreatePrivateRateGoals, SvgAttention, BaseConfirmModal }
}

})